import type { EntitlementKey } from '../api/entitlements';
import type { PermissionKey } from './permissions';
import { routePermissionFor } from './permissionGates';

// ---------------------------------------------------------------------------
// The one table of which pages a plan unlocks. The desktop rail, the mobile
// drawer and the route guard all read it, so a page can't be hidden in one
// place and reachable (or blank) in another. Locked pages are hidden from the
// nav (spec 7: hide by default); a direct URL shows an upgrade card instead.
// Reports stays for everyone — only its advanced tabs are gated, inside the
// page.
// ---------------------------------------------------------------------------

export interface RouteGate {
  /** The route and everything under it ("/projects" covers "/projects/:id"). */
  path: string;
  entitlement: EntitlementKey;
  /** How the upgrade card names the page: "<feature> comes with the Starter plan." */
  feature: string;
  /** "Decisions come…" rather than "Decisions comes…". */
  plural?: boolean;
}

export const ROUTE_GATES: readonly RouteGate[] = [
  // The leadership tools, each on its own entitlement so a plan can carry
  // any of them. Outlook is the drivers and KPIs over the year ahead. People
  // history (interactions, person history) has no web page yet.
  { path: '/decisions', entitlement: 'decisions', feature: 'Decisions', plural: true },
  { path: '/strategy', entitlement: 'strategicDrivers', feature: 'Strategic drivers', plural: true },
  { path: '/horizon', entitlement: 'strategicDrivers', feature: 'Outlook' },
  { path: '/capacity', entitlement: 'capacityPlanning', feature: 'Capacity planning' },
  { path: '/principal-profile', entitlement: 'principalProfile', feature: 'Principal profiles', plural: true },
  { path: '/delegation', entitlement: 'delegation', feature: 'Delegation' },
  { path: '/projects', entitlement: 'projects', feature: 'Projects', plural: true },
  { path: '/ea-workspace', entitlement: 'eaWorkspace', feature: 'The EA workspace' },
  { path: '/transcripts', entitlement: 'transcriptProcessing', feature: 'Transcript processing' },
  { path: '/images', entitlement: 'imageAnalysis', feature: 'Image capture' },
  { path: '/webhooks', entitlement: 'webhooks', feature: 'Webhooks', plural: true },
];

export function routeGateFor(pathname: string): RouteGate | undefined {
  // React Router matches paths regardless of case, so the gate must too
  // (otherwise /Decisions renders the page the lock is meant to cover).
  const path = pathname.toLowerCase();
  return ROUTE_GATES.find(g => path === g.path || path.startsWith(`${g.path}/`));
}

const ADMIN_ONLY_PATHS = new Set(['/prompt-management', '/organisation', '/admin', '/billing', '/webhooks']);

export interface NavVisibility {
  unifiedPlanEnabled: boolean;
  isEaRole: boolean;
  isAdmin: boolean;
  has: (entitlement: EntitlementKey) => boolean;
  /** The user's tenant permissions (lib/permissionGates); absent = nothing restricted. */
  can?: (permission: PermissionKey) => boolean;
}

/** Whether a tenant nav item shows — role rules first, then the plan, then the user's permissions. */
export function isTenantNavItemVisible(path: string, v: NavVisibility): boolean {
  if (path === '/plan' && !v.unifiedPlanEnabled) return false;
  if (path === '/ea-workspace' && !v.isEaRole) return false;
  if (path === '/principal-profile' && !v.isEaRole && !v.isAdmin) return false;
  if (ADMIN_ONLY_PATHS.has(path) && !v.isAdmin) return false;
  const gate = routeGateFor(path);
  if (gate && !v.has(gate.entitlement)) return false;
  const needs = routePermissionFor(path);
  return !needs || !v.can || v.can(needs.permission);
}
